// src/components/faq/PapeleraFAQModal.js
import React, { useEffect } from "react";
import { Modal, Button, Table, Spinner, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  fetchPapelera,
  restaurarFAQ,
  eliminarDefinitivoFAQ,
} from "../../redux/faqSlice";

/**
 * Papelera de FAQs (issue #12). Lista las FAQ enviadas a papelera (soft-delete)
 * y permite restaurarlas o eliminarlas definitivamente.
 * Backend: GET /papelera, POST /:id/restore, DELETE /:id/force (ya existentes).
 */
export default function PapeleraFAQModal({ show, onClose, onCambio }) {
  const dispatch = useDispatch();
  const { papelera = [], loadingPapelera } = useSelector((s) => s.faq);

  useEffect(() => {
    if (show) dispatch(fetchPapelera());
  }, [show, dispatch]);

  const onRestaurar = async (faq) => {
    const r = await Swal.fire({
      title: "¿Restaurar esta FAQ?",
      text: `"${faq.titulo}" volverá al listado.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, restaurar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });
    if (!r.isConfirmed) return;
    try {
      await dispatch(restaurarFAQ(faq.id)).unwrap();
      if (onCambio) onCambio(); // ← avisa al AdminPage que recargue su tabla
      Swal.fire({
        icon: "success",
        title: "FAQ restaurada",
        timer: 1400,
        showConfirmButton: false,
      });
      Swal.fire({
        icon: "success",
        title: "FAQ restaurada",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire("Error", err?.message || "No se pudo restaurar.", "error");
    }
  };

  const onEliminarDefinitivo = async (faq) => {
    const r = await Swal.fire({
      title: "¿Eliminar definitivamente?",
      html: `Se borrará <strong>"${faq.titulo}"</strong> de forma permanente.<br>Esta acción <strong>no se puede deshacer</strong>.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, eliminar para siempre",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });
    if (!r.isConfirmed) return;
    try {
      await dispatch(eliminarDefinitivoFAQ(faq.id)).unwrap();
      Swal.fire({
        icon: "success",
        title: "Eliminada definitivamente",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire("Error", err?.message || "No se pudo eliminar.", "error");
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-trash me-2" aria-hidden="true"></i>
          Papelera de Preguntas Frecuentes
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingPapelera ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
            <p className="mt-2 mb-0 text-muted">Cargando papelera…</p>
          </div>
        ) : papelera.length === 0 ? (
          <Alert variant="info" className="mb-0">
            La papelera está vacía. Las FAQ que elimines aparecerán aquí y
            podrás restaurarlas.
          </Alert>
        ) : (
          <Table striped bordered hover responsive className="small mb-0">
            <thead className="table-dark">
              <tr>
                <th>Título</th>
                <th className="text-center" style={{ width: 220 }}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {papelera.map((faq) => (
                <tr key={faq.id}>
                  <td className="align-middle">{faq.titulo}</td>
                  <td className="text-center">
                    <Button
                      size="sm"
                      variant="outline-success"
                      className="me-2 d-inline-flex align-items-center gap-1"
                      onClick={() => onRestaurar(faq)}
                    >
                      <i className="bi bi-arrow-counterclockwise"></i>
                      Restaurar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      className="d-inline-flex align-items-center gap-1"
                      onClick={() => onEliminarDefinitivo(faq)}
                    >
                      <i className="bi bi-trash3"></i>
                      Eliminar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
